import { provideHttpClient, withFetch } from '@angular/common/http';
import {
    ApplicationConfig,
    EnvironmentProviders,
    importProvidersFrom,
    inject,
    LOCALE_ID,
    provideAppInitializer,
    Provider,
    provideZonelessChangeDetection
} from '@angular/core';
import { provideClientHydration, withEventReplay, withHttpTransferCacheOptions, withIncrementalHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    NavigationError,
    provideRouter,
    RedirectCommand,
    Router,
    withComponentInputBinding,
    withInMemoryScrolling,
    withNavigationErrorHandler
} from '@angular/router';
import { provideDfAccount } from '@df/account/df-account.module';
import { provideDfApi } from '@df/api/df-api.providers';
import { provideDfBrowser } from '@df/browser/df-browser.providers';
import { provideDfCatalogUi } from '@df/catalog-ui/df-catalog-ui.providers';
import { IsBannerPluginService } from '@df/catalog-ui/listing/plugin/banner/is-banner-plugin.service';
import '@df/catalog-ui/related-products/upsell-engine';
import '@df/catalog-ui/related-products/category-engine';
import '@df/catalog-ui/related-products/manual-engine';
import '@df/catalog-ui/related-products/related-engine';
import '@df/catalog-ui/listing/filter/filter-attribute-range';
import { CategoryResource } from '@df/catalog/category/category.resource';
import { CustomformResource } from '@df/catalog/customform/customform.resource';
import { provideDfCatalog } from '@df/catalog/df-catalog.providers';
import { Store } from '@df/catalog/store/store';
import { StoreResource } from '@df/catalog/store/store.resource';
import { App } from '@df/core/app';
import { provideDfCore } from '@df/core/df-core.providers';
import { MetaAlternateService } from '@df/core/meta/meta-alternate.service';
import { StructuralDataService } from '@df/core/meta/structural-data/structural-data.service';
import { Logger } from '@df/core/service/logger';
import { provideDfDom } from '@df/dom/df-dom.providers';
import { provideDfModuleAb } from '@df/module-ab/ab.providers';
import { provideDfModuleAbCms2 } from '@df/module-ab/cms2/ab-cms2.providers';
import { provideDfModuleElasticsearch } from '@df/module-elasticsearch/elasticsearch.providers';
import { IMarkerOptions } from '@df/module-google-maps/google-map/google-map.component.def';
import { provideDfModuleGoogleMaps } from '@df/module-google-maps/google-maps.providers';
import { GoogleRecaptchaService } from '@df/module-google-recaptcha/google-recaptcha.service';
import { DfModuleGtmModule } from '@df/module-gtm/df-module-gtm.module';
import { provideDfModuleImage } from '@df/module-image/image.providers';
import { provideDfModuleProductConfiguration } from '@df/module-product-configuration/df-module-product-configuration.providers';
import { provideDfModuleSentry } from '@df/module-sentry/sentry.providers';
import { provideDfModuleThumbor } from '@df/module-thumbor/df-thumbor.providers';
import { provideDfSearch } from '@df/search/df-search.providers';
import { Basket } from '@df/session/api/basket/basket';
import { Wishlist } from '@df/session/api/wishlist/wishlist';
import { provideDfSession } from '@df/session/df-session.providers';
import { provideDfUi } from '@df/ui/df-ui.providers';
import { provideDfUiModal } from '@df/ui/modal/modal.providers';
import { provideDfUiTranslation } from '@df/ui/translation/translation.providers';
import { provideDfSsr } from '@df/universal/client/df-ssr.providers';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsPageResource } from '@icms/core/resource/page/icms-page.resource';
import * as Sentry from '@sentry/angular';
import { ROUTING_TABLE } from './app.routes';
import './basket/basket-item.decorator';
import { BasketDecorator } from './basket/basket.decorator';
import './catalog/category/category.decorator';
import { NamedError } from './catalog/error/named-error';
import './catalog/page/local/local-page';
import { LocalPageResource } from './catalog/page/local/local-page.resource';
import { isRouteLookupRequest } from './catalog/route-lookup.resolver';
import './catalog/product/product.decorator';
import { provideCmsComponents } from './cms/cms-components.provider';
import { ICMS_CONFIG, routeError } from './icms.config';
import { StructuralDataServiceDecorator } from './meta/structural-data.service.decorator';
import { TrustpilotSchemaObserverService } from './meta/trustpilot-schema-observer.service';
import { ITrustpilotStructuralDataConfig, TRUSTPILOT_STRUCTURAL_DATA_CONFIG } from './meta/trustpilot-review.service';
import './module/campaign-tracking/campaign-tracking.cms-config';
import { CampaignTrackingService } from './module/campaign-tracking/campaign-tracking.service';
import { GoogleRecaptchaServiceDecorator } from './module/google-recaptcha/google-recaptcha.service.decorator';
import { provideSentryTransactionNames } from './module/sentry/transaction-name';
import { GtmEventConvertExperimentsTrigger } from './module/gtm/event/convert-experiments/gtm-event-convert-experiments.trigger';
import { InvocaBootstrapService } from './module/invoca/invoca-bootstrap.service';
import { InvocaRescanService } from './module/invoca/invoca-rescan.service';
import { ProductListingFactory } from '@df/catalog-ui/listing/product-listing.factory';
import { ProductListingFactoryDecorator } from './catalog/listing/product-listing.factory.decorator';
import './catalog/product/plp-trustpilot-reviews/config/product-plp-trustpilot-reviews.config';
import { CATEGORY_LISTING_STEP } from './catalog/listing/category-listing-step';

const providers: (Provider | EnvironmentProviders)[] = [];
if (typeof window !== 'undefined') {
    providers.push(provideZonelessChangeDetection());
}

// Sentry runs browser-side only; app.config.ts is also evaluated during SSR, where `window` is absent.
const sentryEnvironment = typeof window !== 'undefined' ? window.dfConfig?.environment : undefined;

// Session Replay sampling per environment. UAT and staging capture everything so replay can be
// verified end to end; production samples sessions lightly because replay payloads are heavy on the
// self-hosted Sentry instance; local development captures nothing so developer sessions don't upload
// recordings to the shared Sentry project. Anything unrecognised falls back to the production rates.
const replaySampleRates =
    sentryEnvironment === 'development'
        ? { session: 0, onError: 0 }
        : sentryEnvironment === 'uat' || sentryEnvironment === 'staging'
          ? { session: 1.0, onError: 1.0 }
          : { session: 0.1, onError: 1.0 };

// Tracing sampling per environment. The module default works out at roughly 0.001, which is far too
// thin to diagnose Core Web Vitals: over 2 days it yielded 8 real pageloads carrying a CLS
// measurement, and Sentry reports those aggregates with confidence "low" because every figure is
// extrapolated a thousandfold from a handful of events. 0.05 lifts that to a few hundred CLS samples
// a day — enough for a trustworthy p75 per page — while keeping the self-hosted instance to a few
// tens of thousands of spans a day rather than the ~835k a rate of 1.0 would imply. UAT and staging
// sample everything because their traffic is negligible and instrumentation needs verifying there.
// Local development stays off so developer sessions do not report.
//
// Automated browsers are never traced. Over 25–27 Sep HeadlessChrome sent 81 of 133 full-session
// CLS samples, including every one of the worst (up to 2.9), none of which CrUX counts, so they
// skewed the Web Vitals data and spent the sampling budget. `navigator.webdriver` catches
// Puppeteer, Playwright and Selenium even when the user agent has been rewritten.
const isAutomatedBrowser = typeof navigator !== 'undefined' && (navigator.webdriver === true || /HeadlessChrome/.test(navigator.userAgent));

const tracesSampleRate =
    isAutomatedBrowser || sentryEnvironment === 'development'
        ? 0
        : sentryEnvironment === 'uat' || sentryEnvironment === 'staging'
          ? 1.0
          : 0.05;

export const appConfig: ApplicationConfig = {
    providers: [
        // Only the route lookup is carried over from the server render. Without it the browser has
        // to fetch the lookup again before it can show the page it already has, and when that request
        // fails (as it did for Google's renderer) the page is replaced by "page not found" — see
        // RouteLookupResolver. Everything else stays uncached, as dragonfly-universal recommends,
        // to keep the HTML small.
        provideClientHydration(
            withEventReplay(),
            withHttpTransferCacheOptions({ filter: req => isRouteLookupRequest(req.url) }),
            withIncrementalHydration()
        ),
        provideHttpClient(withFetch()),
        provideCmsComponents(),
        provideDfApi(),
        provideAnimations(),
        provideDfBrowser(),
        provideDfCatalog(),
        provideDfCatalogUi({
            listing: {
                resetTop: 0,
                category: {
                    infScrollStep: CATEGORY_LISTING_STEP,
                    infScrollInit: CATEGORY_LISTING_STEP,
                    size: [
                        {
                            size: 1,
                            onSize: ['S']
                        },
                        {
                            size: 2,
                            onSize: ['S', 'M']
                        },
                        {
                            size: 3,
                            onSize: ['M', 'L', 'X']
                        },
                        {
                            size: 4,
                            onSize: ['L', 'X']
                        }
                    ],
                    defaultSize: [2, 3, 4, 4],
                    sort: [
                        {
                            label: 'Recommended',
                            default: true
                        },
                        {
                            label: 'New',
                            code: 'created_at',
                            desc: true
                        },
                        {
                            label: 'Highest price',
                            code: 'price',
                            desc: true
                        },
                        {
                            label: 'Lowest price',
                            code: 'price'
                        }
                    ]
                }
            },

            pdp: {
                configurable: {
                    respectStockSettings: true
                }
            }
        }),
        provideDfCore({
            url: {
                pathRules: [
                    {
                        from: /^bss\//,
                        to: '/static/media/bss/'
                    }
                ]
            },
            // Merchant identity for JSON-LD: populates Organization/WebSite name and
            // the product offer `seller` (previously emitted empty).
            structuralData: {
                name: 'Acoustical Solutions',
                phones: [{ no: '800-782-5742', type: 'customer service' }]
            },
            staleTabReload: {
                maxIdleMs: 8 * 60 * 60 * 1000 // 8 hours
            }
        }),
        provideDfDom(),
        provideDfSsr(),
        provideDfModuleAb({
            convertcom: {
                script: (app: App) => {
                    if (app.dfConfig.environment === 'production') {
                        return 'https://cdn-4.convertexperiments.com/v1/js/10047575-100417754.js';
                    } else {
                        return 'https://cdn-4.convertexperiments.com/v1/js/10047575-100417753.js';
                    }
                }
            }
        }),
        provideDfModuleAbCms2(),
        provideDfModuleElasticsearch(),
        provideDfModuleGoogleMaps({
            apiOptions: {
                libraries: 'places'
            },
            mapOptions: {
                zoom: 5,
                scrollwheel: false
            },
            storeToMarker: (store: Store, options: IMarkerOptions) => {
                return {
                    ...options,
                    info: `
                    <div class="center font-default p-a-1">
                        <h5 class="h4 p-b-1">${store.data?.store_name || 'store name not set'}</h5>
                        <p class="p-b-1 font-2 fs-4">${store.data?.address || 'address not set'}</p>
                        <p class="font-2"><a class="col-2" href="tel:${store.data?.phone || 'phone not set'}">${
                            store.data?.phone || 'phone not set'
                        }</a></p>
                    </div>`
                };
            }
        }),
        provideDfModuleImage(),
        provideDfModuleProductConfiguration(),
        provideDfModuleThumbor(),
        provideDfUi({
            price: {
                thousandSeparator: ','
            }
        }),
        provideDfSession(),
        provideDfUiModal(),
        provideDfSearch(),
        provideDfUiTranslation(),
        provideDfAccount({
            navigation: [
                { icon: 'basket', label: 'Order history', link: '/account/my-orders' },
                { icon: 'account', label: 'Account details', link: '/account/edit' },
                { icon: 'home', label: 'Address book', link: '/account/address-book' },
                { icon: 'credit-card', label: 'Payment methods', link: '/account/payment-methods' },
                { icon: 'wishlist', label: 'Wishlist', link: '/account/wishlist' }
            ],
            historyLoadQty: 1
        }),
        importProvidersFrom(
            ICmsClientModule.forRoot(ICMS_CONFIG),
            DfModuleGtmModule.forRoot({
                includeTax: false
            })
        ),
        { provide: ICmsPageResource, useExisting: LocalPageResource },
        { provide: ProductListingFactory, useExisting: ProductListingFactoryDecorator },
        { provide: Basket, useExisting: BasketDecorator },
        { provide: GoogleRecaptchaService, useClass: GoogleRecaptchaServiceDecorator },
        { provide: StructuralDataService, useExisting: StructuralDataServiceDecorator },
        {
            // Trustpilot identifiers for the server-side product review JSON-LD fetch.
            // `businessUnitId` must match the Trustpilot Business Unit used by the CMS
            // Trustpilot widgets; `jsonLdTemplateId` is Trustpilot's product-review
            // JSON-LD template (distinct from the on-page display widget template).
            provide: TRUSTPILOT_STRUCTURAL_DATA_CONFIG,
            useValue: {
                businessUnitId: '5c01a7bfd3d896000100bd40',
                jsonLdTemplateId: '5717796816f630043868e2e8',
                reviewLanguages: 'en',
                numberOfReviews: 10
            } satisfies ITrustpilotStructuralDataConfig
        },
        {
            provide: LOCALE_ID,
            useFactory: (app: App) => {
                const lang = app.dfConfig.ms?.sites[app.dfConfig.ms.siteId]?.lang?.[0] || 'en';
                return lang === 'x-default' ? 'en' : lang;
            },
            deps: [App]
        },
        provideDfModuleSentry({
            dsn: 'https://fa0367ebc0fc20b3889387fd60090609@sentry.c1a.tomandco.uk/71',
            integrations: [
                // browserTracingIntegration must be re-declared here: provideDfModuleSentry merges via
                // defaultsDeep, which replaces arrays wholesale rather than merging them, so omitting it
                // would silently drop route tracing and INP.
                // Standalone CLS spans report each session's final CLS at page-hide, with the source
                // elements of its largest shifts, instead of the value at the moment the pageload
                // transaction closes. The latter is what left Sentry showing Chrome sessions as clean
                // while CrUX had 14% of homepage visits shifting badly later in the visit.
                Sentry.browserTracingIntegration({ enableInp: true, _experiments: { enableStandaloneClsSpans: true } }),
                // Replays are recorded unredacted — text, input values and media all default to
                // masked/blocked, and support cannot follow a session when every label and field is
                // a grey box. Card and ACH details are the one exception.
                //
                // maskAllInputs is global, so switching it off unmasks every input on the site and
                // these selectors become the only thing keeping card data out of Sentry. They are
                // deliberately layered: <ebizcharge> masks the whole payment subtree including any
                // field added to it later, and the name/autocomplete selectors are a backstop for a
                // card field rendered outside that element or the element being renamed. PAN and CVC
                // must never reach Sentry — widen this list before moving a payment field, not after.
                Sentry.replayIntegration({
                    maskAllText: false,
                    maskAllInputs: false,
                    blockAllMedia: false,
                    mask: ['ebizcharge', '[name^="cc_"]', '[name^="ach_"]', 'input[autocomplete^="cc-"]']
                })
            ],
            tracesSampleRate,
            replaysSessionSampleRate: replaySampleRates.session,
            replaysOnErrorSampleRate: replaySampleRates.onError
        }),
        provideSentryTransactionNames(),
        provideAppInitializer(() => {
            inject(CategoryResource);
            inject(CustomformResource);
            inject(IsBannerPluginService);
            inject(MetaAlternateService);
            inject(StoreResource);
            inject(Wishlist);
            inject(CampaignTrackingService);
            inject(GtmEventConvertExperimentsTrigger);
            inject(InvocaBootstrapService);
            inject(InvocaRescanService);
            inject(TrustpilotSchemaObserverService).init();
        }),
        provideRouter(
            ROUTING_TABLE,
            withInMemoryScrolling({
                scrollPositionRestoration: 'disabled' // we have our own implementation that does that
            }),
            // router error handling
            withNavigationErrorHandler((e: NavigationError) => {
                const targetUrl = `/${routeError}`;
                if (!(e.error instanceof NamedError)) {
                    inject(Logger).info('Unexpected navigation error', e);
                }
                // do not redirect if the error is already on the target url
                if (e.url === targetUrl) {
                    throw e;
                }
                return new RedirectCommand(inject(Router).parseUrl(targetUrl), {
                    skipLocationChange: true,
                    state: { error: e.error }
                });
            }),
            withComponentInputBinding()
        )
    ]
};
